import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslate, useI18n } from '../contexts/I18nContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = 'plancity_translations';

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); }
  catch { return {}; }
};
const writeCache = (cache) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(cache)); } catch { /* ignore */ }
};

/**
 * Translates a plain string (no JSX) using the backend batch endpoint.
 * Renders the result as text (no HTML).
 */
export const T = ({ children }) => {
  const { translateText } = useTranslate();
  if (typeof children !== 'string') return children;
  return <>{translateText(children)}</>;
};

/**
 * Translates an array of HTML chunks. Each chunk is sent in its OWN batch
 * request so the OpenAI response can never be truncated by mixing many
 * sections in a single response. Cached per-chunk in localStorage.
 *
 * If you pass `html` (string) we will auto-split it on lines starting with
 * <h2 (section boundaries) so each section is translated independently.
 */
export const THtml = ({ as: Component = 'div', html, chunks, className }) => {
  const { locale } = useI18n();

  const initialChunks = useMemo(() => {
    if (chunks && Array.isArray(chunks)) return chunks;
    if (!html) return [];
    return html.split(/(?=<h2)/).map((p) => p.trim()).filter(Boolean);
  }, [html, chunks]);

  const [translated, setTranslated] = useState(initialChunks);

  useEffect(() => {
    let cancelled = false;
    if (locale === 'es' || initialChunks.length === 0) {
      setTranslated(initialChunks);
      return () => { cancelled = true; };
    }
    const cache = readCache();
    const out = [...initialChunks];
    // Apply cached translations immediately
    initialChunks.forEach((c, idx) => {
      const k = `${locale}:${c}`;
      if (cache[k]) out[idx] = cache[k];
    });
    setTranslated([...out]);
    // Find chunks that still need translation
    const missing = initialChunks
      .map((c, idx) => ({ c, idx, k: `${locale}:${c}` }))
      .filter((x) => !cache[x.k]);
    if (missing.length === 0) return () => { cancelled = true; };
    // Translate all missing chunks IN PARALLEL (each in its own request to
    // avoid response token truncation, but fired simultaneously)
    Promise.all(
      missing.map(async ({ c, idx, k }) => {
        try {
          const res = await axios.post(`${API_URL}/api/translate/batch`, {
            texts: [c],
            target_lang: locale,
          });
          const t = res.data?.translations?.[c];
          if (t) {
            cache[k] = t;
            out[idx] = t;
            return true;
          }
        } catch (e) { /* keep original */ }
        return false;
      })
    ).then(() => {
      if (cancelled) return;
      writeCache(cache);
      setTranslated([...out]);
    });
    return () => { cancelled = true; };
  }, [initialChunks, locale]);

  return (
    <Component
      className={className}
      dangerouslySetInnerHTML={{ __html: translated.join('\n') }}
    />
  );
};

export default T;
